import React, { useRef, useCallback, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Button, Typography, Spin, Tag, Popover, Radio } from 'antd';
import { LeftOutlined, RightOutlined, ClockCircleOutlined } from '@ant-design/icons';
import './CalendarPreview.css';

const { Text } = Typography;

const TYPE_CONFIG = {
  available: { color: '#52c41a', label: 'Available' },
  unavailable: { color: '#ff4d4f', label: 'Unavailable' },
  'user-event': { color: '#8c8c8c', label: 'Calendar Event' },
};

const formatTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Compute the valid date boundaries from availability settings
const getAvailabilityBounds = (availabilitySettings) => {
  if (!availabilitySettings) return { minDate: null, maxDate: null, allowedDays: null, allowedDates: null };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (availabilitySettings.mode === 'specific_dates' && availabilitySettings.specificDates) {
    const dates = availabilitySettings.specificDates
      .filter((d) => d.date)
      .map((d) => d.date);
    if (dates.length === 0) return { minDate: today, maxDate: null, allowedDays: null, allowedDates: null };
    const sorted = [...dates].sort();
    const minDate = new Date(sorted[0] + 'T00:00:00');
    const maxDate = new Date(sorted[sorted.length - 1] + 'T00:00:00');
    return {
      minDate: minDate > today ? minDate : today,
      maxDate,
      allowedDays: null,
      allowedDates: new Set(dates),
    };
  }

  return { minDate: today, maxDate: null, allowedDays: null, allowedDates: null };
};

const isDayOutOfRange = (date, bounds) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  // Always disable past days
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d < today) return true;

  if (!bounds.minDate && !bounds.maxDate && !bounds.allowedDays && !bounds.allowedDates) return false;

  if (bounds.minDate && d < bounds.minDate) return true;
  if (bounds.maxDate && d > bounds.maxDate) return true;

  if (bounds.allowedDates) {
    const dateStr = d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
    return !bounds.allowedDates.has(dateStr);
  }

  if (bounds.allowedDays) {
    return !bounds.allowedDays.includes(d.getDay());
  }

  return false;
};

const CalendarPreview = ({ schedules, timezone, loading, availableSlotCount, onDateChange, visible, availabilitySettings }) => {
  const calendarRef = useRef(null);
  const [dateLabel, setDateLabel] = React.useState('');
  const [popover, setPopover] = React.useState({ open: false, x: 0, y: 0, events: [] });
  const [canGoPrev, setCanGoPrev] = React.useState(true);
  const [canGoNext, setCanGoNext] = React.useState(true);
  const [currentView, setCurrentView] = React.useState('timeGridWeek');

  const bounds = React.useMemo(() => getAvailabilityBounds(availabilitySettings), [availabilitySettings]);

  // Close popover when drawer closes
  useEffect(() => {
    if (!visible) setPopover((p) => ({ ...p, open: false }));
  }, [visible]);

  const updateNavState = useCallback((api) => {
    if (!api) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // The previous view ends the day before activeStart
    const prevEnd = new Date(api.view.activeStart);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const minBound = bounds.minDate || today;
    setCanGoPrev(prevEnd >= minBound);

    if (bounds.maxDate) {
      const nextStart = new Date(api.view.activeEnd);
      setCanGoNext(nextStart <= bounds.maxDate);
    } else {
      setCanGoNext(true);
    }
  }, [bounds]);

  const handleDatesSet = useCallback((dateInfo) => {
    const start = dateInfo.start;
    const end = new Date(dateInfo.end);
    end.setDate(end.getDate() - 1);
    const viewType = dateInfo.view.type;

    if (viewType === 'timeGridDay') {
      setDateLabel(start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }));
    } else if (viewType === 'dayGridMonth') {
      setDateLabel(start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
    } else {
      const opts = { month: 'short', day: 'numeric' };
      const startStr = start.toLocaleDateString('en-US', opts);
      const endStr = end.toLocaleDateString('en-US', opts);
      const year = start.getFullYear();
      setDateLabel(`${startStr} – ${endStr}, ${year}`);
    }

    updateNavState(calendarRef.current?.getApi());
  }, [updateNavState]);

  const handleViewChange = (e) => {
    const view = e.target.value;
    setCurrentView(view);
    closePopover();
    const api = calendarRef.current?.getApi();
    if (api) {
      api.changeView(view);
    }
  };

  // Re-evaluate nav state when bounds change
  useEffect(() => {
    updateNavState(calendarRef.current?.getApi());
  }, [bounds, updateNavState]);

  const closePopover = useCallback(() => {
    setPopover((p) => ({ ...p, open: false }));
  }, []);

  const dayCellClassNames = useCallback((arg) => {
    if (isDayOutOfRange(arg.date, bounds)) {
      return ['calendar-preview-day-disabled'];
    }
    return [];
  }, [bounds]);

  // Close popover when navigating
  const goPrev = () => {
    if (!canGoPrev) return;
    closePopover();
    const api = calendarRef.current?.getApi();
    if (api) {
      api.prev();
      if (onDateChange) onDateChange(api.view.activeStart.toISOString());
    }
  };

  const goNext = () => {
    if (!canGoNext) return;
    closePopover();
    const api = calendarRef.current?.getApi();
    if (api) {
      api.next();
      if (onDateChange) onDateChange(api.view.activeStart.toISOString());
    }
  };

  const goToday = () => {
    closePopover();
    const api = calendarRef.current?.getApi();
    if (api) {
      api.today();
      if (onDateChange) onDateChange(api.view.activeStart.toISOString());
    }
  };

  // On event click, find all overlapping events at that time and show popover
  const handleEventClick = useCallback((info) => {
    info.jsEvent.preventDefault();
    info.jsEvent.stopPropagation();

    const clickedStart = info.event.start?.getTime();
    const clickedEnd = info.event.end?.getTime();

    // Find all events that overlap with the clicked event's time range
    const overlapping = (schedules || []).filter((s) => {
      const sStart = new Date(s.start).getTime();
      const sEnd = new Date(s.end).getTime();
      return sStart < clickedEnd && sEnd > clickedStart;
    });

    const rect = info.el.getBoundingClientRect();
    const wrapperEl = info.el.closest('.calendar-preview-wrapper');
    const wrapperRect = wrapperEl ? wrapperEl.getBoundingClientRect() : { left: 0, top: 0 };

    setPopover({
      open: true,
      x: rect.left - wrapperRect.left + rect.width / 2,
      y: rect.top - wrapperRect.top,
      events: overlapping.length > 0 ? overlapping : [{
        title: info.event.title,
        start: info.event.startStr,
        end: info.event.endStr,
        extendedProps: info.event.extendedProps,
      }],
    });
  }, [schedules]);

  const popoverContent = (
    <div style={{ maxHeight: 280, overflowY: 'auto', minWidth: 200, maxWidth: 300 }}>
      {popover.events.map((evt, i) => {
        const type = evt.extendedProps?.type || 'user-event';
        const cfg = TYPE_CONFIG[type] || TYPE_CONFIG['user-event'];
        return (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '6px',
              borderBottom: i < popover.events.length - 1 ? '1px solid #f0f0f0' : 'none',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                backgroundColor: cfg.color,
                flexShrink: 0,
                marginTop: 5,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontSize: 13, display: 'block', wordBreak: 'break-word' }}>
                {evt.title}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {formatTime(evt.start)} – {formatTime(evt.end)}
              </Text>
              <Tag
                color={cfg.color}
                style={{ marginLeft: 6, fontSize: 11, lineHeight: '18px', padding: '0 4px' }}
              >
                {cfg.label}
              </Tag>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Spin spinning={loading}>
      <div className="calendar-preview-wrapper" onClick={() => popover.open && closePopover()}>
        <div className="calendar-preview-toolbar">
          <div className="calendar-preview-toolbar-nav">
            <Button size="small" icon={<LeftOutlined />} onClick={goPrev} disabled={!canGoPrev} />
            <Text strong style={{ fontSize: 14, minWidth: 180, textAlign: 'center' }}>{dateLabel}</Text>
            <Button size="small" icon={<RightOutlined />} onClick={goNext} disabled={!canGoNext} />
            <Button size="small" onClick={goToday}>Today</Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag color="green">
              {availableSlotCount} slot{availableSlotCount !== 1 ? 's' : ''} available
            </Tag>
            <Radio.Group
              size="small"
              value={currentView}
              onChange={handleViewChange}
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: <span style={{ fontWeight: 'normal' }}>Day</span>, value: 'timeGridDay' },
                { label: <span style={{ fontWeight: 'normal' }}>Week</span>, value: 'timeGridWeek' },
                { label: <span style={{ fontWeight: 'normal' }}>Month</span>, value: 'dayGridMonth' },
              ]}
            />
          </div>
        </div>

        <div className="calendar-preview-legend">
          <span className="calendar-preview-legend-item">
            <span className="calendar-preview-legend-dot" style={{ backgroundColor: '#52c41a' }} />
            Available
          </span>
          <span className="calendar-preview-legend-item">
            <span className="calendar-preview-legend-dot" style={{ backgroundColor: '#ff4d4f' }} />
            Unavailable
          </span>
          <span className="calendar-preview-legend-item">
            <span className="calendar-preview-legend-dot" style={{ backgroundColor: '#8c8c8c' }} />
            Calendar Event
          </span>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>Click an event for details</Text>
        </div>

        <div style={{ position: 'relative' }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[timeGridPlugin, dayGridPlugin]}
            initialView="timeGridWeek"
            headerToolbar={false}
            editable={false}
            selectable={false}
            events={schedules}
            timeZone={timezone || 'local'}
            height={currentView === 'dayGridMonth' ? 'auto' : 550}
            nowIndicator
            allDaySlot={true}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            eventDisplay="block"
            eventMaxStack={3}
            datesSet={handleDatesSet}
            dayCellClassNames={dayCellClassNames}
            eventClick={handleEventClick}
            eventContent={(arg) => (
              <div style={{ fontSize: 11, padding: '1px 2px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', cursor: 'pointer' }}>
                {arg.event.title}
              </div>
            )}
          />

          {/* Popover positioned absolutely over the calendar */}
          {popover.open && (
            <div
              style={{
                position: 'absolute',
                left: popover.x,
                top: popover.y,
                zIndex: 1050,
                transform: 'translate(-50%, -100%)',
                pointerEvents: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Popover
                open={true}
                content={popoverContent}
                trigger="click"
                placement="top"
                overlayStyle={{ maxWidth: 280 }}
                overlayInnerStyle={{ padding: 0 }}
              >
                <span style={{ display: 'inline-block', width: 1, height: 1 }} />
              </Popover>
            </div>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default CalendarPreview;

// Exported for testing — not part of the public API
export { getAvailabilityBounds, isDayOutOfRange };
